import { useState, useEffect, useRef } from "react";
import "./RefundReturn.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import FallbackImg from "../../components/FallbackImg";
import ProfileLayout from "../../components/profilelayout/ProfileLayout";
import ApiService from "../../components/Api_services";
import { useSelector } from "react-redux";
import { ACCESS_KEYS } from "../../constants";
import { hasAccess } from "../../utils/Permission";
import CustomDropdown from "../../components/customdropdown/CustomDropdown";
import CalendarIcon from "../../assets/icons/CalendarBlank.svg";
import Calendar from "react-calendar";

function getPagination(current, total) {
  const pages = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 4) pages.push("...");
    let start, end;
    if (current <= 4) {
      start = 2;
      end = 5;
    } else if (current >= total - 3) {
      start = total - 4;
      end = total - 1;
    } else {
      start = current - 1;
      end = current + 1;
    }
    for (let i = start; i <= end; i++) {
      if (i > 1 && i < total) pages.push(i);
    }
    if (current < total - 3) pages.push("...");
    pages.push(total);
  }
  return pages;
}

const RefundReturn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Derive page from URL
  const page = parseInt(searchParams.get("page") || "1", 10);
  const activeTab = searchParams.get("status") || "all";

  const [refundList, setRefundList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [page, setCurrentPage] = useState(1)
  const [totalOrders, setTotalOrders] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // This triggers API
  const ordersPerPage = 5;
  const sub_role = useSelector((state) => state.auth.sub_role) || [];
  const is_subuser =
    useSelector((state) => state.auth.user?.is_subuser) || false;
  const hadMyRefundAccess =
    !is_subuser || hasAccess(sub_role, ACCESS_KEYS.MY_RF);

  const [allBranchOptions, setAllBranchOptions] = useState([
    { value: "", label: "All Branch" },
  ]);
  const [selectedBranch, setSelectedBranch] = useState({
    value: "",
    label: "All Branch",
  });
  const [dateRange, setDateRange] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarTempRange, setCalendarTempRange] = useState([null, null]);
  const inputRef = useRef(null);

  const fetchRefunds = async (pageNum = 1, searchText = search) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: pageNum };
      if (searchText) params.search = searchText;
      if (selectedBranch.value) params.branch_id = selectedBranch.value;
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].toISOString().slice(0, 10);
        params.end_date = dateRange[1].toISOString().slice(0, 10);
      }
      if (activeTab && activeTab !== "all") params.status = activeTab;
      const res = await ApiService.getAllRefund(params);
      // 只在数据回来后再 setRefundList
      setRefundList(res.data || []);
      setTotalOrders(res.pagination?.total_records || 0);
    } catch (err) {
      setError("Failed to fetch refund data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds(page, search);
    // eslint-disable-next-line
  }, [page, search, selectedBranch, dateRange, activeTab]);

  useEffect(() => {
    ApiService.getBranchList().then((data) => {
      if (Array.isArray(data)) {
        setAllBranchOptions([
          { value: "", label: "All Branch" },
          ...data.map((item) => ({
            value: item.branch_id,
            label: item.branch_code,
          })),
        ]);
      }
    });
  }, []);

  // When changing tab, update the URL instead of setActiveTab
  const handleTabChange = (tab) => {
    setSearchInput("");
    setSearch("");
    setDateRange(null);
    const params = new URLSearchParams(searchParams);
    params.set("status", tab);
    params.set("page", "1");
    setSearchParams(params, { replace: true });
  };

  // const handlePageChange = (newPage) => {
  //   setCurrentPage(newPage);
  //   window.scrollTo(0, 0); // scroll to top when page changes
  // };

  const handlePageChange = (newPage) => {
    // Update the URL with the new page parameter
    const params = new URLSearchParams(location.search);
    params.set("page", newPage);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let changed = false;

    if (!params.get("status")) {
      params.set("status", "all");
      changed = true;
    }
    if (!params.get("page")) {
      params.set("page", "1");
      changed = true;
    }

    if (changed) {
      // Use replace to avoid adding to browser history
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, []);

  // Filter uppercase to lowercase
  // const filteredRefundList = search
  //   ? refundList.filter((item) =>
  //       item.refund_note.toLowerCase().includes(search.toLowerCase())
  //     )
  //   : refundList;
  const displayedRefundList = refundList;

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    // Update URL to page 1
    const params = new URLSearchParams(location.search);
    params.set("page", 1);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    const params = new URLSearchParams(location.search);
    params.set("page", 1);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    if (searchInput === "") {
      setSearch("");
    }
  }, [searchInput]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-MY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setCalendarOpen(false);
    // Optionally trigger fetchOrders here with range[0] and range[1]
  };

  return (
    <ProfileLayout>
      <div className="refundreturn">
        <div className="refundreturn-content">
          <div className="rr-title-container">
            <div className="myrefund-title">
              <strong>MY REFUND</strong>
            </div>

            {/* <div className="searchbar-refundrtn">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                style={{
                  color: "#798777",
                  fontSize: "18px",
                }}
              />
              <input
                placeholder="Search"
                value={search}
                // onChange={(e) => setSearch(e.target.value)}
                onChange={(e) => {
                  setSearch(e.target.value);
                  // Update URL to page 1 on search change
                  const params = new URLSearchParams(location.search);
                  params.set("page", 1);
                  navigate(`${location.pathname}?${params.toString()}`, {
                    replace: true,
                  });
                }}
              />
              {search && (
                <FontAwesomeIcon
                  icon={faTimes}
                  onClick={clearSearch}
                  style={{
                    color: "#798777",
                    fontSize: "16px",
                    cursor: "pointer",
                    marginRight: "10px",
                  }}
                />
              )}
            </div> */}
          </div>

          {hadMyRefundAccess ? (
            <>
              <div className="refundreturn-tab">
                {["all", "processing", "cancelled", "done"].map((tab) => (
                  <div
                    key={tab}
                    className={`rrtab${activeTab === tab ? " active-tab" : ""}`}
                    onClick={() => handleTabChange(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </div>
                ))}
              </div>

              <div className="refundreturn-line"></div>

              <div className="refundreturn-sub" style={{ marginTop: 18 }}>
                <form onSubmit={handleSearch} className="searchbar-refundrtn">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    style={{ color: "#798777", fontSize: "18px" }}
                  />
                  <input
                    placeholder="Search by Refund No, Total Amount, Item Code"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <FontAwesomeIcon
                      icon={faTimes}
                      onClick={clearSearch}
                      style={{
                        color: "#798777",
                        fontSize: "16px",
                        cursor: "pointer",
                        marginRight: "10px",
                      }}
                    />
                  )}
                </form>

                {/* Date Picker */}
                <form
                  className="rr-date-picker-container"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (calendarTempRange?.[0] && calendarTempRange?.[1]) {
                      handleDateRangeChange(calendarTempRange);
                    }
                  }}
                >
                  <input
                    ref={inputRef}
                    type="text"
                    className="rr-date-range-input"
                    value={
                      calendarTempRange?.[0]
                        ? `${new Date(calendarTempRange[0]).toLocaleDateString(
                            "en-MY",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            },
                          )} - ${
                            calendarTempRange[1]
                              ? new Date(
                                  calendarTempRange[1],
                                ).toLocaleDateString("en-MY", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "End Date"
                          }`
                        : "Select Date Range"
                    }
                    onClick={() => setCalendarOpen((open) => !open)}
                    readOnly
                  />
                  {(calendarTempRange?.[0] || calendarTempRange?.[1]) && (
                    <FontAwesomeIcon
                      icon={faTimes}
                      style={{
                        marginLeft: 8,
                        color: "#aaa",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCalendarTempRange([null, null]);
                        setDateRange([null, null]);
                        setCalendarOpen(false);
                      }}
                    />
                  )}
                  <FallbackImg
                    src={CalendarIcon}
                    alt="calendar"
                    width="22"
                    height="22"
                    style={{ cursor: "pointer" }}
                    onClick={() => setCalendarOpen((open) => !open)}
                  />
                  {calendarOpen && (
                    <div className="refundreturn-calendar">
                      <Calendar
                        selectRange
                        onChange={(range) => {
                          setCalendarTempRange(range);
                          // Focus input after two dates are picked
                          if (range?.[0] && range?.[1] && inputRef.current) {
                            inputRef.current.focus();
                          }
                          setCalendarOpen(false);
                        }}
                        value={calendarTempRange}
                        maxDate={new Date()}
                      />
                    </div>
                  )}
                  {/* Hidden submit button to allow Enter key to trigger onSubmit */}
                  <button
                    type="submit"
                    style={{ display: "none" }}
                    tabIndex={-1}
                  ></button>
                </form>

                <CustomDropdown
                  options={allBranchOptions}
                  value={selectedBranch.value}
                  onChange={(val, opt) => setSelectedBranch(opt)}
                  placeholder="All Branch"
                  className="allorder-dropdown"
                />
              </div>

              {/* <div className="refundreturn-main">
            <MyOrderNav />
          </div>

          <div className="refundreturn-line"></div> */}

              {/* <div className="refundreturn-sub">
            <div className="searchbar-refundrtn">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                style={{
                  color: "#798777",
                  fontSize: "18px",
                }}
              />
              <input
                placeholder="Search"
                value={search}
                // onChange={(e) => setSearch(e.target.value)}
                onChange={(e) => {
                  setSearch(e.target.value);
                  // Update URL to page 1 on search change
                  const params = new URLSearchParams(location.search);
                  params.set("page", 1);
                  navigate(`${location.pathname}?${params.toString()}`, {
                    replace: true,
                  });
                }}
              />
              {search && (
                <FontAwesomeIcon
                  icon={faTimes}
                  onClick={clearSearch}
                  style={{
                    color: "#798777",
                    fontSize: "16px",
                    cursor: "pointer",
                    marginRight: "10px",
                  }}
                />
              )}
            </div>
          </div> */}

              <div className="refundreturn-sub2">
                {refundList.length === 0 && loading ? (
                  <p>Loading...</p>
                ) : error ? (
                  <p style={{ color: "red" }}>{error}</p>
                ) : displayedRefundList.length === 0 ? (
                  <p>No refund/return records found.</p>
                ) : (
                  displayedRefundList.map((item, idx) => (
                    <div key={item.refund_id || idx}>
                      <Link
                        to={`/refundorderdtl/${item.refund_note}?status=${activeTab}`}
                        className="refundreturn-item"
                      >
                        <div
                          style={{ width: 25, display: "inline-block" }}
                        ></div>

                        <div className="refundreturn-leftcenter-container">
                          <div className="refundreturn-tag-main">
                            <div className={`refundreturn-tag ${item.status}`}>
                              {item.status.charAt(0).toUpperCase() +
                                item.status.slice(1)}
                            </div>
                          </div>

                          <div style={{ display: "flex" }}>
                            <div className="refundreturn-left">
                              <div className="refundreturn-orderno">
                                <span className="refundreturn-subtitle">
                                  Refund No
                                </span>
                                <div className="refundreturn-colon">:</div>
                                <div className="refundreturn-value">
                                  {item.refund_note ? item.refund_note : "-"}
                                </div>
                              </div>
                              <div className="refundreturn-status">
                                <span className="refundreturn-subtitle">
                                  Date
                                </span>
                                <div className="refundreturn-colon">:</div>
                                <div className="refundreturn-tag-container">
                                  <div className="refundreturn-value">
                                    {item.done_date
                                      ? `${formatDate(item.done_date)}`
                                      : "-"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="refundreturn-center">
                              <div className="refundreturnitem-code">
                                <span className="refundreturn-subtitle">
                                  Item Code
                                </span>
                                <div className="refundreturn-colon">:</div>
                                <div className="refundreturn-value">
                                  {item.item_code ? item.item_code : "-"}
                                </div>
                              </div>
                              <div className="refundreturnitem-branch">
                                <span className="refundreturn-subtitle">
                                  Branch
                                </span>
                                <div className="refundreturn-colon">:</div>
                                <div className="refundreturn-value">
                                  {item.branch_code ? item.branch_code : "-"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="refundreturn-right">
                          <FallbackImg
                            src={item.item_photo}
                            alt={item.item_code}
                            style={{
                              width: "100px",
                              height: "90px",
                              objectFit: "contain",
                              borderRadius: "5px",
                            }}
                          />

                          {/* <a
                        href={item.redund_note_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="refundreturn-btn"
                        onClick={(e) => e.stopPropagation()} // Prevent the Link from activating
                      >
                        Download PDF
                      </a> */}
                        </div>

                        <div className="rr-nextsign">&gt;</div>
                      </Link>
                    </div>
                  ))
                )}
                <div className="pagination-modern">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  >
                    <span className="arrow">&lt;</span>
                    <span className="prevnext-text">Prev</span>
                  </button>
                  {getPagination(
                    page,
                    Math.ceil(totalOrders / ordersPerPage),
                  ).map((p, idx) =>
                    p === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="pagination-ellipsis"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={`page-${p}-${idx}`}
                        className={`pagination-btn${
                          page === p ? " pagination-current" : ""
                        }`}
                        onClick={() => handlePageChange(p)}
                        disabled={page === p}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === Math.ceil(totalOrders / ordersPerPage)}
                  >
                    <span className="prevnext-text">Next</span>
                    <span className="arrow">&gt;</span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div
              style={{
                minHeight: "60vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ color: "#b71c1c", fontWeight: 600 }}>
                You do not have permission to view My Refund.
              </span>
            </div>
          )}
        </div>
      </div>
    </ProfileLayout>
  );
};

export default RefundReturn;
